import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import date from 'date-and-time';
import { faUsers } from '@fortawesome/pro-light-svg-icons/faUsers';
import { TypedMemo } from '../../../common/types';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { useSMLPaginationParam } from '../../hooks/useSMLPaginationParam';
import { SMLResultsCard } from '../SMLResultsCard/SMLResultsCard';
import { SMLResultsItem } from '../SMLResultsCard/props';
import { PositionChangeElement } from '../PositionChangeElement/PositionChangeElement';
import { useArtistsTopOverall } from '../../hooks/useArtistsTopOverallHook';
import { useSMLCountryParam } from '../../hooks/useSMLCountryParam';
import { useSMLGenreParam } from '../../hooks/useSMLGenreParam';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { datePickerParamName } from '../SMLDatePicker/consts';
import { countryParamName } from '../SMLCountrySelector/consts';
import { genreParamName } from '../SMLGenreSelector/consts';

const paginationParamName = 'overallPage';

const TopOverallArtistCardComponent: React.FC = () => {
  const intl = useIntl();
  const { selectedDate, selectedDateValue } = useSMLDatePickerDateParam();
  const { currentPage } = useSMLPaginationParam(paginationParamName);
  const { countryCode } = useSMLCountryParam();
  const { genre } = useSMLGenreParam();

  const {
    data: artistsData,
    isLoading,
    error,
    refetch,
  } = useArtistsTopOverall({
    page: currentPage,
    date: selectedDate,
  });

  const artists = useMemo(() => artistsData?.artists || [], [artistsData?.artists]);

  const items: SMLResultsItem[] = useMemo(
    () =>
      artists.map((artist: any) => ({
        id: artist.artistMaid,
        imageUrl: artist.imageUrl,
        name: artist.mainArtistName,
        position: artist.overallPosition.current,
        rightElement: (
          <PositionChangeElement
            previousPosition={artist.overallPosition.previous}
            currentPosition={artist.overallPosition.current}
          />
        ),
        chart: { entityType: 'artist' as const, date: selectedDate },
        route: SMLRoutes.artistDetails,
        params: {
          artistId: artist.artistMaid,
          [datePickerParamName]: selectedDateValue,
          ...(countryCode !== undefined ? { [countryParamName]: countryCode } : {}),
          ...(genre !== undefined ? { [genreParamName]: genre } : {}),
        },
      })),
    [artists, selectedDate, selectedDateValue, countryCode, genre]
  );

  const title = intl.formatMessage({
    id: 'app.sml.home.topOverall.title',
    defaultMessage: 'Top Artists Overall',
  });

  const subtitle = date.format(selectedDate, 'MMM DD, YYYY');

  return (
    <SMLResultsCard
      title={title}
      subtitle={subtitle}
      icon={faUsers}
      iconColor="var(--brand-violet-lift)"
      items={items}
      isLoading={isLoading}
      error={error}
      onRetry={() => {
        refetch().catch(console.error);
      }}
      showCountryFilter
      showGenreFilter
      paginationParamName={paginationParamName}
    />
  );
};

export const TopOverallArtistCard = TypedMemo(TopOverallArtistCardComponent);
